<template>
  <p
    v-if="statusMessage"
    class="terrain-autosave__status"
    :class="`terrain-autosave__status--${status}`"
    :role="isFailure ? 'alert' : 'status'"
    aria-live="polite"
  >
    {{ statusMessage }}
  </p>
</template>

<script>
const createIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `terrain-draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export default {
  name: 'AutosaveController',
  props: {
    api: { type: Object, required: true },
    campaignId: { type: String, required: true },
	responseId: { type: String, default: '' },
	prefillCaseId: { type: String, default: '' },
    templateVersionId: { type: String, required: true },
    answers: { type: Array, default: () => [] },
    gpsOriginal: { type: Object, default: null },
    gpsFinal: { type: Object, default: null },
    clientRevision: { type: Number, default: 0 },
    resourceVersion: { type: Number, default: 0 },
    debounceMs: { type: Number, default: 750 },
    // enabled queda en false hasta que el respondente edite una respuesta de
    // verdad (ver TerrainCampaignForm.vue). Sin este freno, la captura
    // silenciosa de GPS_Original al abrir el formulario (o el borrador ya
    // restaurado de una visita anterior) dispara un autoguardado y muestra
    // "Progreso guardado en el servidor" antes de que el respondente haya
    // hecho nada, lo cual confunde más de lo que ayuda.
    enabled: { type: Boolean, default: true },
  },
  data() {
    return {
      timer: null,
      saving: false,
      pending: false,
      status: 'idle',
      latestClientRevision: this.clientRevision,
      latestResourceVersion: this.resourceVersion,
    };
  },
  computed: {
    isSaving() { return this.saving; },
    isFailure() { return ['error', 'offline', 'conflict'].includes(this.status); },
    statusMessage() {
      const messages = {
        saving: 'Guardando progreso…',
        saved: 'Progreso guardado en el servidor.',
        offline: 'Se requiere conexión para guardar el progreso. V1 no guarda borradores sin conexión.',
        conflict: 'El borrador cambió en otro lugar. Recargue la campaña antes de continuar.',
        error: 'No se pudo guardar el progreso. Sus cambios permanecen en esta pantalla.',
      };
      return messages[this.status] || '';
    },
  },
  watch: {
    answers: { deep: true, handler() { if (this.enabled) this.scheduleAutosave(); } },
    gpsOriginal: { deep: true, handler() { if (this.enabled) this.scheduleAutosave(); } },
    gpsFinal: { deep: true, handler() { if (this.enabled) this.scheduleAutosave(); } },
    clientRevision(value) { this.latestClientRevision = value; },
    resourceVersion(value) { this.latestResourceVersion = value; },
  },
  beforeDestroy() { this.clearTimer(); },
  methods: {
    clearTimer() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    },
    scheduleAutosave() {
      if (this.saving) {
        this.pending = true;
        return;
      }
      this.clearTimer();
      this.timer = setTimeout(() => this.saveNow(), this.debounceMs);
    },
    isOnline() {
      return typeof navigator === 'undefined' || navigator.onLine !== false;
    },
    payload() {
      return {
        templateVersionId: this.templateVersionId,
        answers: this.answers,
        ...(this.gpsOriginal ? { gpsOriginal: this.gpsOriginal } : {}),
        ...(this.gpsFinal ? { gpsFinal: this.gpsFinal } : {}),
        clientRevision: this.latestClientRevision,
		...(this.prefillCaseId ? { prefillCaseId: this.prefillCaseId } : {}),
      };
    },
    updateStore(status, draft = null) {
      if (this.$store && this.$store.commit) this.$store.commit('terrain/SET_AUTOSAVE_STATE', { status, draft });
    },
    async saveNow() {
      this.clearTimer();
      if (this.saving) {
        this.pending = true;
        return false;
      }
      if (!this.isOnline()) {
        this.status = 'offline';
        this.updateStore(this.status);
        this.$emit('save-failed', { status: this.status });
        return false;
      }

      this.saving = true;
      this.status = 'saving';
      this.updateStore(this.status);
      const payload = this.payload();
      const idempotencyKey = createIdempotencyKey();
      try {
		const response = this.responseId
		  ? await this.api.saveCorrectionDraft(this.campaignId, this.responseId, payload, this.latestResourceVersion, idempotencyKey)
		  : await this.api.saveDraft(this.campaignId, payload, this.latestResourceVersion, idempotencyKey);
        const draft = response && response.data ? response.data : response;
        this.latestClientRevision = draft.clientRevision;
        this.latestResourceVersion = draft.resourceVersion;
        this.status = 'saved';
        this.updateStore(this.status, draft);
        this.$emit('saved', draft);
        return true;
      } catch (error) {
        const code = error && error.response && error.response.data && error.response.data.code;
        this.status = code === 'TERRAIN_STALE_RESOURCE' ? 'conflict' : 'error';
        this.updateStore(this.status);
        this.$emit('save-failed', { status: this.status, error });
        return false;
      } finally {
        this.saving = false;
        if (this.pending) {
          this.pending = false;
          this.scheduleAutosave();
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-autosave__status { margin: 0; font-size: var(--terrain-font-caption); }
.terrain-autosave__status--saved { color: var(--color-success); }
.terrain-autosave__status--error,
.terrain-autosave__status--offline,
.terrain-autosave__status--conflict { color: var(--color-danger); }
</style>
